import { useState, useEffect } from 'react';
import BlogCard from '../components/BlogCard';
import BlogGenerator from '../components/BlogGenerator';
import { Search, Filter, Sparkles } from 'lucide-react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { db } from '../services/firebase';
import useAuthStore from '../stores/authStore';
import usePageMeta from '@/utils/usePageMeta';

/**
 * Transform Firestore blog_posts document to blog format
 */
const transformPostToBlog = (doc) => {
  const post = { id: doc.id, ...doc.data() };

  const publishedAt =
    post.createdAt?.toDate?.().toISOString() ||
    post.publishedAt?.toDate?.().toISOString() ||
    post.created_at ||
    new Date().toISOString();

  return {
    id: post.id,
    title: post.title || 'Untitled',
    excerpt: post.excerpt || post.content?.substring(0, 200) || '',
    content: post.content || '',
    coverImage: post.imageUrl || post.image_url || post.coverImage || null,
    videoUrl:   post.videoUrl || null,
    category: post.category || post.platform || 'General',
    publishedAt,
    readTime: Math.ceil((post.content?.length || 0) / 1000) || 1,
  };
};

const Blogs = () => {
  usePageMeta({
    title: 'Blog — Data, AI & Content Automation | DataLogicHub',
    description:
      'Articles on data engineering, AI content generation, and automation from DataLogicHub. Practical, hands-on guides.',
  });

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showGenerator, setShowGenerator] = useState(false);

  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);

      // Bounded read (UIUX audit M1): newest 48 posts instead of the whole
      // collection. Both blog writers set createdAt via serverTimestamp().
      const snapshot = await getDocs(
        query(collection(db, 'blog_posts'), orderBy('createdAt', 'desc'), limit(48))
      );
      const transformedBlogs = snapshot.docs
        .filter(doc => doc.data().published !== false)
        .map(transformPostToBlog)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
      setBlogs(transformedBlogs);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setError(err.message || 'Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const categories = ['all', ...new Set(blogs.map(blog => blog.category))];

  const filteredBlogs = blogs
    .filter(blog => {
      const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || blog.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f4f8] to-[#e0e7ff] py-12">

      {/* AI Blog Generator modal — admin only */}
      {showGenerator && (
        <BlogGenerator
          onClose={() => setShowGenerator(false)}
          onPublished={() => {
            setShowGenerator(false);
            fetchBlogs();
          }}
        />
      )}

      {/* Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-[#1f2937] mb-4 bg-gradient-to-r from-[#667eea] to-[#764ba2] bg-clip-text text-transparent">
            Latest Posts
          </h1>
          <p className="text-lg text-[#6b7280] max-w-2xl mx-auto">
            Discover insights, tutorials, and stories about data analytics, AI, and business intelligence
          </p>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-[20px] shadow-[0_8px_25px_rgba(0,0,0,0.1)] p-6 mb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9ca3af]" size={20} />
              <input
                type="text"
                placeholder="Search posts..."
                aria-label="Search posts"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#667eea] text-[#1f2937]"
              />
            </div>

            {/* Category Filter */}
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9ca3af]" size={20} />
              <select
                value={selectedCategory}
                aria-label="Filter by topic"
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-[#e5e7eb] rounded-[10px] focus:outline-none focus:ring-2 focus:ring-[#667eea] appearance-none bg-white text-[#1f2937]"
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Topics' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-16">
            <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-[#667eea] border-t-transparent"></div>
            <p className="mt-4 text-[#6b7280] text-lg">Loading posts...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-[#fee2e2] border border-[#ef4444] rounded-[15px] p-6 mb-8">
            <h3 className="text-[#991b1b] font-semibold mb-2">Error Loading Posts</h3>
            <p className="text-[#dc2626]">{error}</p>
          </div>
        )}

        {/* Posts Grid */}
        {!loading && !error && (
          <>
            {filteredBlogs.length === 0 ? (
              <div className="text-center py-16">
                <p className="text-[#6b7280] text-lg">No posts found matching your criteria.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
                  {filteredBlogs.map((blog) => (
                    <BlogCard key={blog.id} blog={blog} />
                  ))}
                </div>

                <div className="text-center text-[#6b7280]">
                  Showing {filteredBlogs.length} of {blogs.length} posts
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Floating admin button — visible only to admin users */}
      {isAdmin && (
        <button
          onClick={() => setShowGenerator(true)}
          title="Generate AI Blog Post"
          className="fixed bottom-8 right-8 flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white text-sm font-semibold rounded-full shadow-[0_4px_20px_rgba(102,126,234,0.5)] hover:shadow-[0_6px_28px_rgba(102,126,234,0.6)] hover:-translate-y-0.5 transition-all z-40"
        >
          <Sparkles size={18} />
          Generate Blog
        </button>
      )}
    </div>
  );
};

export default Blogs;
